'use client';

import { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Avatar } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardContext from '@/contexts/CardContext';
import CardHeader from '@mui/material/CardHeader';
import DataContext from '@/contexts/DataContext';
import Grid from '@mui/material/Unstable_Grid2';
import { icons } from '@/components/dashboard/layout/nav-icons';
import { InfoTooltip } from '../typography/info-tooltip';
import { ProcessCardObject } from './process-card-object';
import { fetchTooltip } from '@/lib/dataHandlers/DataFetchers';
import Typography from '@mui/material/Typography';
import { useComponentLogger } from '@/lib/component-logger';
import { useTheme, useMediaQuery } from '@mui/material';
import { useMaxLines } from '@/hooks/use-max-lines';

export function GroupedCard({ config={}, params={}, isLoading = false, setIsLoading, selectable=false, onDeselectAll, onDeselectOne, onSelectAll, onSelectOne, selected=[] }) {
    const logger = useComponentLogger("Grouped Card");

    if (!config || Object.keys(config).length === 0) return null;
    const { currentProject, selectedViewParams } = useContext(DataContext);
    const validSizes = [33, 66, 50, 100];

    const HeaderIcon = config.icon && icons[config.icon] ? icons[config.icon] : null; 
    const HeaderColor = config.title ? "black" : "white";
    const [cardTitle, setCardTitle] = useState(config.title || ''); // title can be overidden by table, hence why this is passed in a CardContext to the child components
    const [tooltip, setTooltip] = useState(null);
    const titleOpacity = config.title ? '100%' : '0%';

    const cardWidthXS = Math.round((config.desktopSize / 100) * 12) || 12;
    const cardWidthDesktop = (config.desktopSize !== null || config.desktopSize !== undefined) ? Math.round((config.desktopSize / 100) * 12) : 12;

    logger.debug('desktop size', cardWidthDesktop, config.desktopSize)
    // const contentPadding = config.objects[0]?.type === 'table' ? '0' : '';
    // const contentPaddingBottom = (config.objects?.length === 1 && config.objects[0].type === "image") ? null : '2rem';
    const nonPaddedObjects = ["image", "table", "overlay", "map"];
    const contentPadding = nonPaddedObjects.includes(config.objects[0]?.type) ? '0' : '1.5rem';
    const contentPaddingBottom = nonPaddedObjects.includes(config.objects[0]?.type) ? '0' : '2.5rem';
    const contentAlign = (config.objects?.length === 1 && config.objects[0].type === "image") ? 'center' : 'flex-start';

    // on small screens, we want images to fill the entire card with no whitespace/padding
    const contentSize = (config.objects?.length === 1 && config.objects[0].type === "image") ? {sm: 'auto', md: '100%'} : '100%';

    logger.debug('grouped config', config, params, HeaderIcon, contentAlign);

    // if overflow is missing, treat it as true
    // false if overflow = false
    const overflow = (config.overflow === null || config.overflow === undefined || config.overflow === "true");

    
    if (cardWidthDesktop === 0) return null;

    const processTooltip = async() => {
        let tooltipContent = await fetchTooltip(currentProject, params, config);
        // // logger.debug('tooltip data', tooltipContent);
        if (tooltipContent) setTooltip(tooltipContent);
    }

    useEffect(() => {
        logger.debug('group config', config);
        processTooltip().catch(() => {});
    }, []);

    return (
        <CardContext.Provider value={{cardTitle, setCardTitle}}>
            <Grid xs={12} lg={cardWidthDesktop} >
            {/* <Card sx={{width: {xs: cardWidthXS, sm: cardWidthSM}, float: 'left'}}> */}
            <Card sx={{width: '100%', height: '100%', float: 'left', }}>
            {config?.title || config?.icon ? 
                <CardHeader 
                    disableTypography
                    title={<Typography variant="h6" color={'text.primary'} sx={{opacity: titleOpacity, display: 'flex', alignItems: 'center'}}>
                        {cardTitle || ""}
                        {tooltip ? <InfoTooltip content={tooltip} /> : <></>}
                    </Typography>}
                    // throws error if we directly pass icons['iconName']
                    avatar={HeaderIcon ? 
                        <Avatar> 
                            <HeaderIcon fontSize="var(--Icon-fontSize)"/>
                        </Avatar>
                        : null
                    } 
                /> 
                : <></>}
            <CardContent sx={{ 
                height: contentSize,
                width: contentSize,
                '&:last-child': { paddingBottom: contentPaddingBottom }, // CardContent adds an extra space at the bottom otherwise
                padding:`0 ${contentPadding} ${contentPaddingBottom}`
            }}  >  
                <Grid container spacing={2} height='100%' alignItems={contentAlign} justifyContent={contentAlign}>
                    {processCardContent(config, params, overflow, isLoading, setIsLoading, selectable, selected, onDeselectAll, onDeselectOne, onSelectAll, onSelectOne,)}
                </Grid>
            </CardContent>
            </Card>
            </Grid>
        </CardContext.Provider>
    );
}

function processCardContent(config, params, overflow, isLoading=false, setIsLoading, selectable, selected, onDeselectAll, onDeselectOne, onSelectAll, onSelectOne,) {
    // // logger.debug('card row', config, overflow, typeof(overflow));
    const logger = useComponentLogger("Grouped Card");

    const lineHeight = 1.2;

    const { handleLineCount, getMaxLines } = useMaxLines();

    const titleMinHeight = `${getMaxLines() * lineHeight}em`;

    return config.objects.map((object, index) => {
        // calculate object width
        // grids have 12 columns. width 12 is 100%
        const totalCols = 12;
        const calculatedWidth = Math.round((object.size / 100) * totalCols);
        let widthXS;
        let widthSM;
        if (!object.size) {
            // width = totalCols;
            widthXS = totalCols;
            widthSM = totalCols;
        } else {
            if (overflow === false) {
                // width = calculatedWidth
                widthXS = calculatedWidth;
                widthSM = calculatedWidth;
            } else {
                // width = {
                //     xs: totalCols,
                //     sm: calculatedWidth
                // }
                widthXS = totalCols;
                widthSM = calculatedWidth;
            }
        }

        logger.debug('card width', widthXS, widthSM, object);

        return (
            <Grid xs={widthXS} sm={widthSM} key={index}>
                <ProcessCardObject cardObject={object} params={params} isLastObject={index === (config.objects.length - 1 )} objectCount={config.objects.length} isLoading={isLoading} setIsLoading={setIsLoading} selectable={selectable}  onDeselectAll={onDeselectAll} onDeselectOne={onDeselectOne} onSelectAll={onSelectAll} onSelectOne={onSelectOne} selected={selected} notifyParentLineCount={(breakpoint, lines) => handleLineCount(index, breakpoint, lines)} titleMinHeight={titleMinHeight} lineHeight={lineHeight}/>
            </Grid>
        );

    });
    
}


